import { memo, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils/cn";

export interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface MultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  triggerTextClassName?: string;
  summaryMode?: "labels" | "count";
}

const MultiSelect = memo(function MultiSelect({
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled,
  className,
  triggerTextClassName = "text-sm",
  summaryMode = "labels",
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<string>("");
  const searchRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      // focus the search input when opened
      setTimeout(() => searchRef.current?.focus(), 0);
    } else {
      // clear filter when closed
      setFilter("");
    }
  }, [isOpen]);

  function toggleValue(optValue: string) {
    const opt = options.find((o) => o.value === optValue);
    if (opt && opt.disabled) return; // ignore toggles for disabled options

    if (value.includes(optValue)) {
      onChange(value.filter((v) => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  }

  const selectedLabels = options
    .filter((opt) => value.includes(opt.value))
    .map((opt) => opt.label);

  const summary =
    selectedLabels.length === 0
      ? placeholder
      : summaryMode === "count"
        ? `${selectedLabels.length} of ${options.length} selected`
        : selectedLabels.length <= 2
        ? selectedLabels.join(", ")
        : `${selectedLabels.slice(0, 2).join(", ")} +${selectedLabels.length - 2} more`;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-md border border-neutral-200 bg-white px-3 font-sans text-neutral-900 outline-none transition-colors duration-150",
          triggerTextClassName,
          "focus:border-primary-500 focus:ring-[3px] focus:ring-primary-100",
          "disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:opacity-50",
          "dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100",
        )}
      >
        <span
          className={cn(
            "truncate text-left",
            selectedLabels.length === 0 && "text-slate-400",
          )}
        >
          {summary}
        </span>
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-4 w-4 shrink-0 text-slate-400"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && !disabled && (
        <div className="absolute left-0 top-full z-dropdown mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-neutral-200 bg-white p-1 shadow-card-hover dark:border-neutral-800 dark:bg-neutral-900">
          <div className="px-2 py-1">
            <input
              ref={searchRef}
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Type to filter…"
              className="w-full rounded-md border border-neutral-200 px-2 py-1 text-sm outline-none dark:border-neutral-800 dark:bg-neutral-900"
            />
          </div>
          {options.length === 0 && (
            <p className="px-2 py-1.5 text-xs text-slate-400">No options</p>
          )}
          {options
            .filter((opt) =>
              !filter || opt.label.toLowerCase().includes(filter.toLowerCase()) || String(opt.value).toLowerCase().includes(filter.toLowerCase()),
            )
            .map((opt) => (
            <label
              key={opt.value}
              className={cn(
                "flex items-center gap-2 rounded px-2 py-1.5 text-sm",
                opt.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800",
              )}
            >
              <input
                type="checkbox"
                checked={value.includes(opt.value)}
                onChange={() => toggleValue(opt.value)}
                disabled={opt.disabled}
                className="h-3.5 w-3.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              <span className={cn("truncate", opt.disabled ? "text-slate-400" : "text-neutral-700 dark:text-neutral-200")}>
                {opt.label}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
});

export default MultiSelect;
